import { lazy, Suspense } from 'react';
import { SEO } from '@/components/SEO';
import {
  buildOrganizationJsonLd,
  buildSoftwareApplicationJsonLd,
  buildWebPageJsonLd,
  buildFAQPageJsonLd,
} from '@/lib/seo';
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Trophy,
  Building2,
  DollarSign,
  Users,
  BarChart3,
  CheckCircle,
  Smartphone,
  Shield,
  Zap,
  QrCode,
  TrendingUp,
  Star,
  Calendar,
  Target,
} from "lucide-react";

import { useAuth } from "@/hooks/useAuth";

import { VelloClientBadge } from "@/components/sales/VelloClientBadge";

import screenshotArena from "@/assets/screenshot-arena-dashboard.webp";
import screenshotBracket from "@/assets/bracket-hero-preview.webp";

// Lazy-loaded — abaixo do fold
const VelloClientSection = lazy(() =>
  import("@/components/sales/VelloClientSection").then((m) => ({ default: m.VelloClientSection }))
);
const FaqSection = lazy(() =>
  import("@/components/seo/FaqSection").then((m) => ({ default: m.FaqSection }))
);

const HOME_FAQ = [
  {
    question: "O que é o Vello Play?",
    answer:
      "O Vello Play é uma plataforma de gestão esportiva completa para arenas e organizadores de torneios. Centraliza reservas de quadras, inscrições online, chaveamento automático, financeiro, controle de mensalistas, bar/PDV e o portal do atleta — tudo em um só sistema.",
  },
  {
    question: "Quais esportes a plataforma atende?",
    answer:
      "Beach tennis, padel, futevôlei, tênis, futsal, society, vôlei e outros esportes de quadra. Cada modalidade tem regras, formatos de torneio e critérios de classificação configuráveis.",
  },
  {
    question: "Quanto custa um sistema de gestão para arena esportiva?",
    answer:
      "O Vello Play oferece planos sob assinatura para arenas e organizadores, com modelos compatíveis com pequenas arenas e operações maiores. Atletas usam a plataforma gratuitamente para se inscrever em torneios e reservar quadras.",
  },
  {
    question: "É possível organizar torneios de beach tennis com chaveamento automático?",
    answer:
      "Sim. O Vello Play gera chaveamentos automáticos nos formatos eliminatória simples, eliminatória dupla, fase de grupos com classificação, repescagem e mata-mata, com critérios de desempate por esporte.",
  },
  {
    question: "Como funciona a reserva online de quadras?",
    answer:
      "Cada arena ganha um link público de agendamento onde clientes consultam horários disponíveis em tempo real, escolhem a modalidade e pagam via Pix ou cartão. A agenda da arena é atualizada instantaneamente, sem risco de overbooking.",
  },
  {
    question: "Substitui planilhas e grupos de WhatsApp?",
    answer:
      "Sim. Inscrições, comunicação, financeiro, presença em aulas e resultados de torneios passam a viver em um único painel. O envio automático de avisos no WhatsApp continua disponível, integrado à plataforma.",
  },
];

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen">
      <SEO
        title="Vello Play | Plataforma de Gestão Esportiva para Arenas e Torneios"
        description="Plataforma completa de gestão esportiva: reservas de quadras, inscrições online, chaveamento automático, ERP financeiro, mensalistas e portal do atleta. Para arenas e organizadores de torneios de beach tennis, padel, futevôlei e mais."
        canonicalPath="/"
        keywords="plataforma de gestão esportiva, sistema para arena esportiva, software para torneios, gestão de arenas, plataforma para eventos esportivos, software para beach tennis, sistema para padel, gestão de quadras esportivas"
        jsonLd={[
          buildOrganizationJsonLd(),
          buildSoftwareApplicationJsonLd(),
          buildWebPageJsonLd(
            "Vello Play — Plataforma de Gestão Esportiva",
            "Plataforma de gestão esportiva para arenas e organizadores: reservas, inscrições, chaveamento automático, financeiro e portal do atleta.",
            "/",
          ),
          buildFAQPageJsonLd(HOME_FAQ),
        ]}
      />
      
      {/* ═══════════════════════════════════════════
          HERO — Impactful, visual, clear
      ═══════════════════════════════════════════ */}
      <section className="relative overflow-hidden border-b border-border/50">
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-primary/[0.06] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-primary/[0.03] rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 pt-10 pb-12 md:pt-16 md:pb-16 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — Copy */}
            <div className="max-w-xl">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <Badge variant="outline" className="border-primary/30 text-primary px-4 py-1.5 text-sm font-medium backdrop-blur-sm">
                  <Zap className="h-3.5 w-3.5 mr-1.5" />
                  Gestão esportiva completa
                </Badge>
                <VelloClientBadge />
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-[3.5rem] lg:text-[3.8rem] font-bold leading-[1.08] tracking-tight mb-6">
               A plataforma de gestão esportiva da sua{" "}
               <span className="text-primary">arena e torneios.</span>
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-md">
                Reservas de quadras, inscrições online, chaveamento automático, financeiro e portal do atleta — tudo em um só sistema para arenas, organizadores e atletas.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  size="lg"
                  className="btn-gradient-primary px-8 py-6 text-base font-semibold touch-manipulation shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
                  onClick={() => navigate("/autenticacao?tab=cadastro")}
                >
                  Começar Agora
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 text-base touch-manipulation border-border/60 hover:bg-accent/50"
                  onClick={() => navigate("/torneios-publicos")}
                >
                  Explorar Torneios
                </Button>
              </div>
            </div>

            {/* Right — Product preview */}
            <div className="relative hidden lg:block">
              <div className="relative">
                {/* Main screenshot */}
                <div className="rounded-xl overflow-hidden shadow-2xl shadow-black/40 border border-border/30 ring-1 ring-white/[0.05]">
                  <img
                    src={screenshotArena}
                    alt="Painel de gestão de arena Vello Play"
                    width={1280}
                    height={800}
                    {...({ fetchpriority: "high" } as any)}
                    decoding="async"
                    loading="eager"
                    className="w-full h-auto"
                  />
                </div>
                {/* Floating secondary card — bracket preview destaque */}
                <div className="absolute -bottom-12 -left-16 w-[380px] xl:w-[440px] rounded-xl overflow-hidden shadow-2xl shadow-black/70 border-2 border-primary/20 ring-1 ring-white/[0.08]">
                  <img
                    src={screenshotBracket}
                    alt="Chaveamento de torneio"
                    width={880}
                    height={550}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF removido */}

      {/* ═══════════════════════════════════════════
          3 PASSOS — Como funciona
      ═══════════════════════════════════════════ */}
      <section className="border-b border-border/30 bg-muted/10">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
              Tudo o que você precisa em <span className="text-primary">3 frentes</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Uma plataforma única que conecta cliente, arena e organizador
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                step: "01",
                icon: Users,
                title: "Portal do Cliente",
                desc: "Inscrições, histórico de torneios, ranking, reservas de quadras e busca de duplas — tudo na mão do atleta.",
                cta: "Sou Atleta",
                path: user ? "/painel" : "/autenticacao?tab=cadastro",
              },
              {
                step: "02",
                icon: Building2,
                title: "Gestão de Arenas",
                desc: "Agenda visual, mensalistas, aulas, PDV de bar e financeiro integrado em um só painel.",
                cta: "Tenho uma Arena",
                path: user ? "/minhas-arenas" : "/solucao/arenas",
              },
              {
                step: "03",
                icon: Trophy,
                title: "Gestão de Torneios",
                desc: "Inscrições com pagamento, chaveamento automático, placar ao vivo e relatórios financeiros.",
                cta: "Organizo Torneios",
                path: user ? "/torneios" : "/solucao/torneios",
              },
            ].map(({ step, icon: Icon, title, desc, cta, path }) => (
              <Card
                key={title}
                className="relative p-8 border-border/50 hover:border-primary/40 transition-all hover:shadow-xl hover:shadow-primary/5 cursor-pointer group overflow-hidden"
                onClick={() => navigate(path)}
              >
                <span className="absolute top-4 right-5 text-5xl font-bold text-muted-foreground/15 group-hover:text-primary/20 transition-colors">
                  {step}.
                </span>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-xl mb-3">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">{desc}</p>
                <Button variant="outline" size="sm" className="group-hover:border-primary/50 group-hover:text-primary transition-colors">
                  {cta}
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          MODULE 1 — Arena Management
      ═══════════════════════════════════════════ */}
      <section className="border-b border-border/30">
        <div className="container mx-auto px-4 py-10 md:py-14">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Screenshot */}
            <div className="rounded-xl overflow-hidden shadow-2xl shadow-black/30 border border-border/30 ring-1 ring-white/[0.05]">
              <img
                src={screenshotArena}
                alt="Dashboard de gestão de arena"
                loading="lazy"
                width={1280}
                height={800}
                className="w-full h-auto"
              />
            </div>

            {/* Copy */}
            <div className="max-w-lg">
              <Badge variant="outline" className="mb-4 text-primary border-primary/30 text-xs font-semibold uppercase tracking-wider">
                <Building2 className="h-3 w-3 mr-1.5" />
                Gestão de Arena
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-tight">
                Controle total da sua arena em tempo real
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Agenda visual de quadras, mensalistas, turmas de aula com check-in por QR Code e link público de reservas — sem planilhas, sem retrabalho.
              </p>
              <ul className="space-y-3.5 mb-8">
                {[
                  "Agenda visual por quadra e horário",
                  "Link público de reserva online",
                  "Gestão de turmas, aulas e mensalistas",
                  "Cobrança online de alunos (Pix e cartão)",
                  "PDV para bar e restaurante com controle de estoque",
                  "Check-in digital com QR Code",
                  "Financeiro integrado com fluxo de caixa",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(user ? "/minhas-arenas" : "/solucao/arenas")}
                className="btn-gradient-primary shadow-md shadow-primary/10"
              >
                {user ? "Acessar Arenas" : "Conhecer Gestão de Arena"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          MODULE 2 — Tournament Management
      ═══════════════════════════════════════════ */}
      <section className="border-b border-border/30 bg-muted/10">
        <div className="container mx-auto px-4 py-10 md:py-14">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <div className="max-w-lg order-2 lg:order-1">
              <Badge variant="outline" className="mb-4 text-primary border-primary/30 text-xs font-semibold uppercase tracking-wider">
                <Trophy className="h-3 w-3 mr-1.5" />
                Gestão de Torneios
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-tight">
                Do primeiro inscrito ao troféu — sem planilha
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Crie torneios profissionais em minutos, receba inscrições com pagamento online, gere chaves automáticas e acompanhe placares ao vivo.
              </p>
              <ul className="space-y-3.5 mb-8">
                {[
                  "Inscrições online com Pix, cartão e parcelamento",
                  "Chaveamento automático (eliminatória, grupos, repescagem)",
                  "Placar ao vivo e atualização em tempo real",
                  "Ranking, classificação e estatísticas automáticas",
                  "Lotes de preço, cupons de desconto e controle de vagas",
                  "Repasse financeiro e relatórios prontos",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(user ? "/torneios" : "/solucao/torneios")}
                className="btn-gradient-primary shadow-md shadow-primary/10"
              >
                {user ? "Acessar Torneios" : "Conhecer Gestão de Torneios"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Screenshot */}
            <div className="order-1 lg:order-2 rounded-xl overflow-hidden shadow-2xl shadow-black/30 border border-border/30 ring-1 ring-white/[0.05]">
              <img
                src={screenshotBracket}
                alt="Chaveamento de torneio Vello Play"
                loading="lazy"
                width={1280}
                height={800}
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MODULE 3 — Athlete + Financial removido */}

      {/* Bonificação Vello Sports */}
      <Suspense fallback={<div className="h-32" />}>
        <VelloClientSection productHint="produtos para a arena ou para o torneio na Vello Sports" />
      </Suspense>

      {/* ═══════════════════════════════════════════
          CTA — Three entry paths
      ═══════════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/[0.04] rounded-full blur-[100px]" />
        </div>

        <div className="container mx-auto px-4 py-10 md:py-14 relative">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Comece agora. <span className="text-primary">Escolha seu caminho.</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Independente do seu perfil, a Vello Play tem o ambiente certo pra você
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Users, title: "Sou Atleta", desc: "Explore torneios, acompanhe resultados, ranking e reserve quadras.", badge: "Gratuito", path: "/autenticacao?tab=cadastro", authPath: "/painel" },
              { icon: Building2, title: "Tenho uma Arena", desc: "Gerencie quadras, reservas, alunos, turmas e financeiro.", badge: "Planos disponíveis", path: "/autenticacao?tab=cadastro", authPath: "/minhas-arenas" },
              { icon: Trophy, title: "Organizo Torneios", desc: "Crie torneios profissionais com inscrição, chaveamento e pagamento.", badge: "Planos disponíveis", path: "/autenticacao?tab=cadastro", authPath: "/torneios" },
            ].map(({ icon: Icon, title, desc, badge, path, authPath }) => (
              <Card
                key={title}
                className="p-6 text-center hover:shadow-xl hover:shadow-primary/5 transition-all border border-border/50 hover:border-primary/30 cursor-pointer group"
                onClick={() => navigate(user ? authPath : path)}
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                  <Icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{desc}</p>
                <Badge variant="secondary" className="font-medium">{badge}</Badge>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button
              size="lg"
              className="btn-gradient-primary px-10 py-6 text-base font-semibold touch-manipulation shadow-lg shadow-primary/20"
              onClick={() => navigate("/autenticacao?tab=cadastro")}
            >
              Criar Conta
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ — SEO + intenção comercial */}
      <Suspense fallback={<div className="h-48" />}>
        <FaqSection
          items={HOME_FAQ}
          subtitle="Tudo o que arenas, organizadores e atletas costumam perguntar antes de começar."
        />
      </Suspense>

      {/* Internal linking — SEO */}
      <section aria-labelledby="related-heading" className="border-t border-border/30 bg-muted/10">
        <div className="container mx-auto px-4 py-10">
          <h2 id="related-heading" className="sr-only">Explore o Vello Play</h2>
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm">
            <li><a href="/solucao/arenas" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">Sistema de gestão para arenas</a></li>
            <li><a href="/solucao/torneios" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">Plataforma para torneios esportivos</a></li>
            <li><a href="/solucao/erp-financeiro" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">ERP financeiro esportivo</a></li>
            <li><a href="/solucao/portal-atleta" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">Portal do atleta</a></li>
            <li><a href="/torneios-publicos" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">Torneios abertos</a></li>
            <li><a href="/atletas" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">Buscar atletas</a></li>
            <li><a href="/times" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">Times</a></li>
            <li><a href="/ranking-geral" className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline">Ranking geral</a></li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Index;
